/**
 * **Click Spark** — kleines Linien-Feuerwerk auf einem Canvas. Ohne React,
 * damit die Animation nie ein Re-Render auslöst; der Provider in
 * `components/common/ClickSpark` hält die Engine in einem Ref.
 *
 * Werte und Easing nach dem ClickSpark-Effekt von obsidianui.dev.
 */

export type SparkEasing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'

export interface SparkOptions {
  /** CSS-Farbe. Der Provider liest sie sonst aus `--space-700` am Element. */
  color: string
  /** Anzahl der Strahlen. */
  count: number
  /** Wie weit die Strahlen nach außen fliegen (px). */
  radius: number
  /** Länge eines Strahls zu Beginn (px). */
  size: number
  /** ms */
  duration: number
  easing: SparkEasing
  lineWidth: number
}

export const DEFAULT_SPARK_OPTIONS: Omit<SparkOptions, 'color'> = {
  count: 8,
  radius: 15,
  size: 10,
  duration: 400,
  easing: 'ease-out',
  lineWidth: 2,
}

interface Burst extends SparkOptions {
  x: number
  y: number
  start: number
}

export interface SparkEngine {
  burst(x: number, y: number, options: SparkOptions): void
  destroy(): void
}

export function ease(t: number, easing: SparkEasing): number {
  switch (easing) {
    case 'linear': return t
    case 'ease-in': return t * t
    case 'ease-in-out': return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
    default: return t * (2 - t)
  }
}

export function createSparkEngine(canvas: HTMLCanvasElement): SparkEngine {
  const win = canvas.ownerDocument.defaultView ?? window
  const ctx = canvas.getContext('2d')
  const reducedMotion = win.matchMedia?.('(prefers-reduced-motion: reduce)')
  let bursts: Burst[] = []
  let rafId: number | null = null
  let width = 0
  let height = 0

  // Gemessen wird erst beim Burst: ein Canvas, das im Leerlauf nichts zeichnet,
  // braucht keinen Resize-Listener.
  function fit() {
    const dpr = win.devicePixelRatio || 1
    width = win.innerWidth
    height = win.innerHeight
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr
      canvas.height = height * dpr
    }
    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  function draw(now: number) {
    if (!ctx) return
    ctx.clearRect(0, 0, width, height)
    bursts = bursts.filter(b => {
      const elapsed = now - b.start
      if (elapsed >= b.duration) return false
      const p = ease(Math.max(0, elapsed) / b.duration, b.easing)
      const dist = p * b.radius
      const len = b.size * (1 - p)
      ctx.strokeStyle = b.color
      ctx.lineWidth = b.lineWidth
      ctx.lineCap = 'round'
      ctx.beginPath()
      for (let i = 0; i < b.count; i++) {
        const angle = (2 * Math.PI * i) / b.count
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)
        ctx.moveTo(b.x + dist * cos, b.y + dist * sin)
        ctx.lineTo(b.x + (dist + len) * cos, b.y + (dist + len) * sin)
      }
      ctx.stroke()
      return true
    })
    rafId = bursts.length > 0 ? win.requestAnimationFrame(draw) : null
  }

  return {
    burst(x, y, options) {
      if (!ctx || reducedMotion?.matches) return
      if (rafId === null) fit()
      bursts.push({ ...options, x, y, start: win.performance.now() })
      if (rafId === null) rafId = win.requestAnimationFrame(draw)
    },
    destroy() {
      if (rafId !== null) win.cancelAnimationFrame(rafId)
      rafId = null
      bursts = []
    },
  }
}
